import { createContext, useEffect, useState, useRef } from "react";
import {
  setAuthTokenGetter,
  getAuthTokenHolder,
  refreshAccessToken,
  resetRefreshCache,
} from "../store/authTokenHolder";

const AuthContext = createContext({});

/** Access token süresi dolmadan bu kadar ms önce refresh tetiklenir */
const REFRESH_BEFORE_MS = 90 * 1000;

// Odak tazelemesinde (izin senkronu) yeni token yalniz yetkiyi etkileyen bir
// alan degistiyse auth'a yazilir. Neden (#142): RTK Query sorgulari token'i
// argüman olarak aliyor; her yeni token yeni önbellek anahtari demek ve sekmeye
// her dönüste TUM sorgular yeniden cekiliyordu (sayfa yenilenmis gibi).
// Eski token süresi dolana kadar gecerli; süre sonu yenilemesi ayrica calisir.
const sortedKey = (list) => (Array.isArray(list) ? [...list].sort().join("|") : "");
export const isSameAccess = (prev, user) => {
  if (!prev || !user) return false;
  if (user.permissions && sortedKey(user.permissions) !== sortedKey(prev.permissions)) return false;
  if (user.role && user.role !== prev.role) return false;
  if (user.subRole && user.subRole !== prev.subRole) return false;
  if (user.firmPanelAdmin !== undefined && user.firmPanelAdmin !== prev.firmPanelAdmin) return false;
  if (user.firmName !== undefined && user.firmName !== prev.firmName) return false;
  return true;
};

export const AuthProvider = ({ children }) => {
  const [auth, setAuth] = useState(() => {
    const auth = localStorage.getItem("auth");
    return auth ? JSON.parse(auth) : false;
  });

  const parseJwtPayload = (token) => {
    try {
      const base64Url = token.split(".")[1];
      if (!base64Url) return null;
      const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split("")
          .map((c) => `%${(`00${c.charCodeAt(0).toString(16)}`).slice(-2)}`)
          .join("")
      );
      return JSON.parse(jsonPayload);
    } catch (error) {
      return null;
    }
  };

  useEffect(() => {
    localStorage.setItem("auth", JSON.stringify(auth));
    // Oturum kapandığında (logout / session-expired / setAuth(null)) paylaşılan
    // refresh önbelleğini temizle; aksi halde cooldown penceresi içinde bir
    // sonraki kullanıcıya eski token dönebilir.
    if (!auth?.accessToken) resetRefreshCache();
  }, [auth]);

  const refreshInProgressRef = useRef(false);

  useEffect(() => {
    if (!auth?.accessToken) return undefined;

    const payload = parseJwtPayload(auth.accessToken);
    const expMs = payload?.exp ? payload.exp * 1000 : null;
    if (!expMs) return undefined;

    const remainingMs = expMs - Date.now();

    // Token zaten süresi dolmuşsa (örn. sayfayı kapatıp tekrar açtıysa) önce refresh dene; cookie geçerliyse oturum sürer
    if (remainingMs <= 0) {
      if (refreshInProgressRef.current) return undefined;
      refreshInProgressRef.current = true;
      refreshAccessToken()
        .then((result) => {
          const holder = getAuthTokenHolder();
          if (result && holder?.setNewToken) holder.setNewToken(result.token, result.user);
          else setAuth(null);
        })
        .catch(() => setAuth(null))
        .finally(() => { refreshInProgressRef.current = false; });
      return undefined;
    }

    const fireAt = Math.max(0, remainingMs - REFRESH_BEFORE_MS);

    const timer = setTimeout(async () => {
      if (refreshInProgressRef.current) return;
      refreshInProgressRef.current = true;
      try {
        const result = await refreshAccessToken();
        const holder = getAuthTokenHolder();
        if (result && holder?.setNewToken) {
          holder.setNewToken(result.token, result.user);
          // Effect yeni token ile tekrar çalışacak, yeni timer kurulacak
        } else {
          setAuth(null);
        }
      } catch (_) {
        setAuth(null);
      } finally {
        refreshInProgressRef.current = false;
      }
    }, fireAt);

    return () => clearTimeout(timer);
  }, [auth?.accessToken, setAuth]);

  // İzinleri otomatik tazele: sekmeye/pencereye geri dönünce refreshAccessToken çağrılır.
  // refresh endpoint izinleri DB'den yeniden okuyup yeni token'a gömer; böylece admin bir
  // kullanıcının iznini değiştirdiğinde, o kullanıcı sekmeye döndüğü an izinleri güncellenir
  // (manuel "İzinleri Güncelle" butonuna gerek kalmaz). Aşırı isteğe karşı throttle uygulanır.
  const lastPermRefreshRef = useRef(0);
  const authRef = useRef(auth);
  authRef.current = auth;
  useEffect(() => {
    if (!auth?.accessToken) return undefined;
    const MIN_INTERVAL_MS = 30 * 1000;
    const maybeRefresh = async () => {
      if (document.visibilityState !== "visible") return;
      if (refreshInProgressRef.current) return;
      const now = Date.now();
      if (now - lastPermRefreshRef.current < MIN_INTERVAL_MS) return;
      lastPermRefreshRef.current = now;
      refreshInProgressRef.current = true;
      try {
        const result = await refreshAccessToken();
        const holder = getAuthTokenHolder();
        // Yetki değişmediyse token'ı auth'a yazma — sorgular yeniden çekilmesin (#142).
        if (result && holder?.setNewToken && !isSameAccess(authRef.current, result.user)) {
          holder.setNewToken(result.token, result.user);
        }
      } catch (_) {
        // Sessizce geç; token süresi dolmuşsa 401 → session-expired akışı devreye girer
      } finally {
        refreshInProgressRef.current = false;
      }
    };
    document.addEventListener("visibilitychange", maybeRefresh);
    window.addEventListener("focus", maybeRefresh);
    return () => {
      document.removeEventListener("visibilitychange", maybeRefresh);
      window.removeEventListener("focus", maybeRefresh);
    };
  }, [auth?.accessToken]);

  // 401 → refresh → token + user güncellemesi için store'a setNewToken verilir
  useEffect(() => {
    setAuthTokenGetter({
      // İstekte her zaman güncel token (#142) — sorgu argümanındaki token eski kalabilir
      // (önbellek anahtarı token'sız olduğu için sorgu yeniden kurulmaz).
      getToken: () => authRef.current?.accessToken || null,
      setNewToken: (token, user) =>
        setAuth((prev) => {
          if (!prev) return null;
          const updated = { ...prev, accessToken: token };
          if (user) {
            if (user.permissions) updated.permissions = user.permissions;
            if (user.role) updated.role = user.role;
            if (user.subRole) updated.subRole = user.subRole;
            if (user.firmPanelAdmin !== undefined) updated.firmPanelAdmin = user.firmPanelAdmin;
            if (user.firmName !== undefined) updated.firmName = user.firmName;
          }
          return updated;
        }),
    });
    return () => setAuthTokenGetter(null);
  }, []);

  useEffect(() => {
    const PUBLIC_PATHS = ["/davet", "/sifre-sifirla", "/gizlilik-politikasi"];
    const handleSessionExpired = () => {
      const path = window.location.pathname;
      if (PUBLIC_PATHS.some((p) => path.startsWith(p))) return;
      setAuth(null);
    };
    window.addEventListener("session-expired", handleSessionExpired);
    return () => window.removeEventListener("session-expired", handleSessionExpired);
  }, []);

  return (
    <AuthContext.Provider value={{ auth, setAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
